import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, ExternalLink, Tag, TrendingUp, ChevronRight, Loader2,
} from "lucide-react";

const LifestylePage = () => {
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // /lifestyle/me/commissions is no longer fetched — the member
        // earnings strip it fed was removed (STARSEED-CAPMARKETS-080).
        const [c, p] = await Promise.all([
          api.get("/lifestyle/categories"),
          api.get("/lifestyle/partners"),
        ]);
        setCategories(c.data.categories || []);
        setPartners(p.data.partners || []);
      } catch {
        toast.error("Failed to load lifestyle marketplace");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = activeCat === "all" ? partners : partners.filter((p) => p.category === activeCat);
  const featured = partners.filter((p) => p.featured).slice(0, 3);
  const heroPartner = featured[0];

  const handleClick = async (partner) => {
    try {
      const r = await api.post("/lifestyle/click", { partner_id: partner.id });
      window.open(r.data.redirect_url || partner.referral_url, "_blank", "noopener,noreferrer");
    } catch {
      // Even if logging fails, still open the link — UX over telemetry
      window.open(partner.referral_url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-20 text-center text-slate-400">
          <Loader2 className="inline-block animate-spin" size={20} /> Loading marketplace…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="lifestyle-page">
        {/* Hero */}
        {heroPartner && (
          <Card
            className="relative overflow-hidden border-0 text-white shadow-glow-lg cursor-pointer group"
            style={{
              background: `linear-gradient(135deg, ${heroPartner.hero_color || "#0055FF"} 0%, #10B981 140%)`,
            }}
            onClick={() => handleClick(heroPartner)}
            data-testid="lifestyle-hero"
          >
            <div className="absolute -right-32 -top-32 h-72 w-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />
            <CardContent className="relative p-10 md:p-14">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} />
                <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
                  Featured · {heroPartner.commission_rate ? `Member offer · ${heroPartner.commission_rate}` : "Limited offer"}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-serif tracking-tight">{heroPartner.name}</h1>
              <p className="text-white/85 mt-2 text-base md:text-lg max-w-xl">
                {heroPartner.blurb || heroPartner.description}
              </p>
              <Button
                className="mt-6 bg-white text-slate-900 hover:bg-white/90 group-hover:scale-105 transition-transform"
                onClick={(e) => { e.stopPropagation(); handleClick(heroPartner); }}
                data-testid="lifestyle-hero-cta"
              >
                Open offer <ExternalLink size={14} className="ml-1" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Member "My Earnings" strip (Pending / Confirmed / Paid / Total earned)
            REMOVED — STARSEED-CAPMARKETS-080. It was a live member-facing
            commission-payout dashboard, which is exactly what the attorney
            review directed be removed; the affiliate bookkeeping behind it stays
            admin-side. */}

        {/* Category tabs */}
        <Tabs value={activeCat} onValueChange={setActiveCat}>
          <TabsList className="flex flex-wrap h-auto" data-testid="lifestyle-categories">
            <TabsTrigger value="all">All ({partners.length})</TabsTrigger>
            {categories.map((c) => (
              <TabsTrigger key={c.key} value={c.key} data-testid={`cat-tab-${c.key}`}>
                <span className="mr-1">{c.emoji}</span>
                {c.label} ({c.partner_count})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Partner grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="lifestyle-grid">
          {filtered.length === 0 ? (
            <p className="col-span-full text-center text-slate-400 py-10">No partners in this category yet.</p>
          ) : filtered.map((p) => (
            <PartnerCard key={p.id} partner={p} onOpen={handleClick} />
          ))}
        </div>

        {/* Footer note */}
        <p className="text-xs text-slate-400 text-center">
          By clicking a partner you'll be redirected to their site. These are independent
          affiliate offers; EchoSense Capital Club does not control their terms.
        </p>
      </div>
    </DashboardLayout>
  );
};

const PartnerCard = ({ partner, onOpen }) => (
  <Card
    className="bg-white border-slate-200 card-hover cursor-pointer overflow-hidden"
    onClick={() => onOpen(partner)}
    data-testid={`partner-card-${partner.slug}`}
  >
    <div
      className="h-20 relative"
      style={{ background: `linear-gradient(135deg, ${partner.hero_color || "#0055FF"} 0%, #10B981 140%)` }}
    >
      <div className="absolute inset-0 bg-white/5" />
      {partner.featured && (
        <Badge className="absolute top-2 right-2 bg-white/95 text-slate-900 border-0 text-[10px]">
          FEATURED
        </Badge>
      )}
    </div>
    <CardContent className="p-4 -mt-8 relative">
      <div className="h-14 w-14 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center overflow-hidden">
        {partner.logo_url ? (
          <img src={partner.logo_url} alt={partner.name} className="h-10 w-10 object-contain" />
        ) : (
          <span className="text-slate-700 font-semibold">{partner.name[0]}</span>
        )}
      </div>
      <h3 className="mt-3 text-base font-semibold text-slate-900">{partner.name}</h3>
      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{partner.blurb || partner.description}</p>
      <div className="flex items-center justify-between mt-3">
        {partner.commission_rate ? (
          <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
            <TrendingUp size={10} className="mr-1" />
            Member offer · {partner.commission_rate}
          </Badge>
        ) : (
          <span className="text-xs text-slate-400">
            <Tag size={10} className="inline mr-1" /> Affiliate offer
          </span>
        )}
        <ChevronRight size={14} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
      </div>
    </CardContent>
  </Card>
);

export default LifestylePage;
